import { z } from 'zod';
import { ChecklistTaskStatus } from './phases';

const optionalText = (max: number) => z.string().trim().min(1).max(max).optional();

const optionalObject = <T extends z.ZodRawShape>(shape: T) => z.object(shape).strict().optional();

export const NotaryEmailLegalPartySchema = z
  .object({
    fullName: optionalText(200),
    documentType: optionalText(30),
    documentNumber: optionalText(50),
    address: optionalText(500),
    civilStatus: optionalText(150),
    profession: optionalText(150),
  })
  .strict();

export const NotaryEmailRepresentationSchema = z
  .object({
    representativeName: optionalText(200),
    representedName: optionalText(200),
    authority: z.enum(['POWER_OF_ATTORNEY', 'OTHER']).optional(),
    details: optionalText(500),
  })
  .strict();

export const NotaryEmailPartyGroupSchema = z
  .object({
    parties: z.array(NotaryEmailLegalPartySchema).max(10).optional(),
    relationshipStatement: optionalText(500),
    representations: z.array(NotaryEmailRepresentationSchema).max(10).optional(),
  })
  .strict();

const SpanishDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
  }, 'Invalid calendar date');

export const GenerateNotaryEmailDraftRequestSchema = z
  .object({
    sender: optionalObject({
      fullName: optionalText(200),
      phone: optionalText(50),
    }),
    dates: optionalObject({
      transparencyAct: optionalText(250),
      saleDeed: optionalText(250),
    }),
    sellers: NotaryEmailPartyGroupSchema.optional(),
    buyers: NotaryEmailPartyGroupSchema.optional(),
    property: optionalObject({
      description: optionalText(500),
      address: optionalText(500),
      cadastralReference: optionalText(100),
      registry: optionalObject({
        office: optionalText(250),
        propertyNumber: optionalText(100),
        volume: optionalText(100),
        book: optionalText(100),
        folio: optionalText(100),
        entry: optionalText(100),
      }),
    }),
    transaction: optionalObject({
      totalPriceCents: z.number().int().positive().optional(),
      transferAmountCents: z.number().int().nonnegative().optional(),
      notaryCosts: z.enum(['BUYER', 'SELLER', 'SHARED', 'OTHER']).optional(),
      notaryCostsDetails: optionalText(250),
      propertyTaxes: z.enum(['PRORATED', 'BUYER', 'SELLER', 'OTHER']).optional(),
      propertyTaxesDetails: optionalText(250),
      earnestMoney: optionalObject({
        amountCents: z.number().int().positive().optional(),
        proofMediaIds: z.array(z.string().uuid()).max(10).optional(),
      }),
    }),
    powers: optionalObject({
      status: z.enum(['NONE', 'EXISTS', 'UNKNOWN']).optional(),
      details: optionalText(1000),
    }),
    mortgage: optionalObject({
      bank: optionalText(200),
      fein: optionalText(500),
    }),
    community: optionalObject({
      status: z.enum(['CONSTITUTED', 'NOT_CONSTITUTED', 'UNKNOWN']).optional(),
      details: optionalText(500),
    }),
    energyCertificate: optionalObject({
      status: z.enum(['AVAILABLE', 'PENDING', 'NOT_APPLICABLE', 'UNKNOWN']).optional(),
      availableOn: SpanishDateSchema.optional(),
      details: optionalText(500),
    }),
    documentNotes: z.array(z.string().trim().min(1).max(500)).max(20).optional(),
    additionalNotes: z.array(z.string().trim().min(1).max(1000)).max(10).optional(),
  })
  .strict();

export type GenerateNotaryEmailDraftRequestBody = z.infer<
  typeof GenerateNotaryEmailDraftRequestSchema
>;
export type NotaryEmailLegalParty = z.infer<typeof NotaryEmailLegalPartySchema>;
export type NotaryEmailPartyGroup = z.infer<typeof NotaryEmailPartyGroupSchema>;
export type NotaryEmailRepresentation = z.infer<typeof NotaryEmailRepresentationSchema>;

export type NotaryEmailDraftWarningCode =
  | 'MISSING_FACT'
  | 'INCONSISTENT_PAYMENT'
  | 'INCOMPLETE_PRESENTATION'
  | 'EXPECTED_DOCUMENT_MISSING'
  | 'PENDING_INVESTOR_INVITATION';

export interface NotaryEmailDraftWarning {
  code: NotaryEmailDraftWarningCode;
  path?: string;
  taskSlug?: string;
  message: string;
}

export interface NotaryEmailDraftAttachment {
  mediaId: string;
  taskSlug: string;
  taskLabel: string;
  fileName: string;
  mimeType: string;
  url: string;
}

export interface NotaryEmailDraftDTO {
  subject: string;
  text: string;
  html: string;
  readiness: {
    ready: boolean;
    pendingRequiredTasks: Array<{
      id: string;
      slug: string;
      label: string;
      status: ChecklistTaskStatus;
    }>;
  };
  warnings: NotaryEmailDraftWarning[];
  attachments: NotaryEmailDraftAttachment[];
}

export interface GenerateNotaryEmailDraftResponse {
  data: NotaryEmailDraftDTO;
}
